import { useEffect, useRef, useState } from "react";
import { observer, inject } from "mobx-react";
import styled from "styled-components";
import { Text } from "../common/UI";
import { useTranslation } from "react-i18next";
import { SearchInput } from "../common/UI/Input";

const ItemContainer = styled("div")(({ theme }) => ({
  marginTop: 20,
  display: "flex",
  flexDirection: "column",
  maxHeight: 360,
  width: "33rem",
  overflowX: "auto",
  position: "absolute",
  zIndex: 9999,
  top: 98,
  backgroundColor: "#ffffff",
  boxShadow: "rgba(0, 0, 0, 0.12) 0px 9px 76px 0px",

  "&::-webkit-scrollbar-thumb": {
    background: theme.color.red,
    borderRadius: "20px",
    scrollbarWidth: "thin",
    width: "5px",
    height: "1%",
  },

  "&::-webkit-scrollbar": {
    backgroundColor: "#F4F5F5",
    borderRadius: "20px",
    scrollbarWidth: "thin",
    width: "5px",
  },
}));

const ListItemRow = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  padding: 15,
  gap: 10,
  borderBottom: "1px solid #E8E8E8",
  cursor: "pointer",
  transition: "background-color 120ms ease",

  "&:hover": {
    backgroundColor: "#F7F6F6",
  },
  "&:focus-visible": {
    outline: "none",
    backgroundColor: "#F7F6F6",
  },
  "&:last-of-type": {
    borderBottom: "none",
  },
}));

const SearchList = ({
  productsStore,
  showSearchList,
  setShowSearchList,
  onPick,
  list,
  value,
  searchTerm,
  isRtl,
  handleChange,
  handleKeyDown,
  handleSearch,
}) => {
  // Prefer the caller-provided search term (used by the product-page
  // typeahead, where we deliberately don't mutate productsStore.searchedTerm
  // until the user submits). Fall back to the store so existing callers
  // still get highlighting without passing the prop.
  const highlightTerm = searchTerm ?? productsStore.searchedTerm;
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  const { i18n } = useTranslation();
  const [isSearchByPart, setSearchByPart] = useState(false);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    productsStore.setIsSearchByPart(isSearchByPart);
  }, [isSearchByPart, productsStore]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSearchList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setShowSearchList]);

  const boldWordsInText = (text, wordsToBold) => {
    const textArray = text?.split(" ");

    return (
      <>
        {textArray?.map((word, index) => {
          if (
            wordsToBold?.some((boldWord) => word?.toLowerCase().includes(boldWord.toLowerCase()))
          ) {
            return (
              <strong style={{ fontSize: 17 }} key={index}>
                {word}{" "}
              </strong>
            );
          }
          return <span key={index}>{word} </span>;
        })}
      </>
    );
  };

  const ListItem = ({ item, onPick, isSearchByPart }) => {
    const titleText = !!isSearchByPart ? item?.PARTNAME : item.ZACP_MPARTNAME;
    const descriptionText = !isSearchByPart
      ? i18n.language === "he"
        ? item?.ZACP_DESHEB
        : item.ZACP_DESENG
      : i18n.language === "he"
        ? item?.ZACP_DESHEBD
        : item.ZACP_DESENGD;
    const displayLabel = [titleText, descriptionText].filter(Boolean).join(" - ");

    return (
      <ListItemRow
        role="option"
        tabIndex={0}
        onClick={() => {
          onPick(item, displayLabel);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onPick(item, displayLabel);
          }
        }}
      >
        <Text.Medium size={15} style={{ textAlign: "start" }}>
          {boldWordsInText(
            !!isSearchByPart ? item?.PARTNAME : item.ZACP_MPARTNAME,

            highlightTerm?.split(" ")?.filter((e) => !!e)
          )}
        </Text.Medium>
        <Text.Medium size={15} style={{ textAlign: "start" }}>
          {boldWordsInText(
            !isSearchByPart
              ? i18n.language === "he"
                ? item?.ZACP_DESHEB
                : item.ZACP_DESENG
              : i18n.language === "he"
                ? item?.ZACP_DESHEBD
                : item.ZACP_DESENGD,

            highlightTerm?.split(" ")?.filter((e) => !!e)
          )}
        </Text.Medium>
      </ListItemRow>
    );
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        marginInlineStart: 25,
        borderRadius: 10,
        padding: 16,
        flex: 1,
      }}
    >
      <SearchInput
        placeholder="search_placeholder"
        width={"33rem"}
        isRtl={isRtl}
        value={value ?? ""}
        onChange={handleChange}
        onClick={handleSearch}
        onKeyDown={handleKeyDown}
        setSearchByPart={setSearchByPart}
        isSearchByPart={isSearchByPart}
      />
      {showSearchList && (
        <ItemContainer>
          {list?.map((item, index) => (
            <ListItem
              key={index}
              item={item}
              onPick={onPick}
              isSearchByPart={productsStore.isSearchByPart}
            />
          ))}
        </ItemContainer>
      )}
    </div>
  );
};

export default inject("productsStore")(observer(SearchList));
